import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { 
  reportService, 
  ReportFilters, 
  SummaryData, 
  DailyHoursData, 
  ProjectDistributionData, 
  TeamCostDistributionData 
} from '@/services/reportService'

// 組件導入
import ReportFiltersComponent from './components/ReportFilters'
import SummaryCards from './components/SummaryCards'
import DailyHoursChart from './components/DailyHoursChart'
import ProjectDistributionChart from './components/ProjectDistributionChart'
import TeamCostDistributionChart from './components/TeamCostDistributionChart'
import EnhancedTimeEntriesDetailTable from './components/EnhancedTimeEntriesDetailTable'

const ReportsPage: React.FC = () => {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER'

  // 狀態管理
  const [filters, setFilters] = useState<ReportFilters>(() => {
    const defaultRange = reportService.getDefaultDateRange('thisMonth')
    return {
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
      // 管理員預設選擇自己
      ...(isAdmin && user?.id ? { userId: user.id } : {})
    }
  })

  const [summaryData, setSummaryData] = useState<SummaryData>()
  const [dailyHoursData, setDailyHoursData] = useState<DailyHoursData[]>([])
  const [projectDistributionData, setProjectDistributionData] = useState<ProjectDistributionData[]>([])
  const [teamCostDistributionData, setTeamCostDistributionData] = useState<TeamCostDistributionData[]>([])

  const [loading, setLoading] = useState({
    summary: false,
    dailyHours: false,
    projectDistribution: false,
    teamCostDistribution: false
  })

  const [error, setError] = useState<string | null>(null)

  // 載入數據
  const loadData = async () => {
    try {
      setError(null)
      
      // 同時載入所有數據
      const promises = [
        // 統計摘要
        (async () => {
          setLoading(prev => ({ ...prev, summary: true }))
          try {
            const data = await reportService.getSummaryData(filters)
            setSummaryData(data)
          } finally {
            setLoading(prev => ({ ...prev, summary: false }))
          }
        })(),

        // 每日工時
        (async () => {
          setLoading(prev => ({ ...prev, dailyHours: true }))
          try {
            const data = await reportService.getDailyHoursData(filters)
            setDailyHoursData(data)
          } finally {
            setLoading(prev => ({ ...prev, dailyHours: false }))
          }
        })(),

        // 專案分佈
        (async () => {
          setLoading(prev => ({ ...prev, projectDistribution: true }))
          try {
            const data = await reportService.getProjectDistribution(filters)
            setProjectDistributionData(data)
          } finally {
            setLoading(prev => ({ ...prev, projectDistribution: false }))
          }
        })(),

        // 團隊成員花費分佈 (僅管理員)
        ...(isAdmin ? [(async () => {
          setLoading(prev => ({ ...prev, teamCostDistribution: true }))
          try {
            const data = await reportService.getTeamCostDistribution({
              startDate: filters.startDate,
              endDate: filters.endDate,
              projectIds: filters.projectIds,
              status: filters.status
            })
            setTeamCostDistributionData(data)
          } finally {
            setLoading(prev => ({ ...prev, teamCostDistribution: false }))
          }
        })()] : [])
      ]

      await Promise.all(promises)
    } catch (error) {
      console.error('Failed to load report data:', error)
      setError(error instanceof Error ? error.message : '載入報表數據失敗')
    }
  }

  // 當篩選條件改變時重新載入數據
  useEffect(() => {
    loadData()
  }, [filters, isAdmin])

  const handleFiltersChange = (newFilters: ReportFilters) => {
    setFilters(newFilters)
  }

  const handleRefresh = () => {
    loadData()
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-secondary-800">報表分析</h1>
        <div className="flex space-x-4">
          <button
            onClick={handleRefresh}
            className="btn-secondary"
            disabled={Object.values(loading).some(Boolean)}
          >
            {Object.values(loading).some(Boolean) ? '載入中...' : '重新整理'}
          </button>
          <button className="btn-primary">
            匯出報表
          </button>
        </div>
      </div>

      {/* 錯誤提示 */}
      {error && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <span className="text-red-600">⚠️</span>
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* 篩選器 */}
      <ReportFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      {/* 統計卡片 */}
      <SummaryCards
        data={summaryData}
        loading={loading.summary}
      />

      {/* 每日工時圖表 */}
      <DailyHoursChart
        data={dailyHoursData}
        loading={loading.dailyHours}
      />

      {/* 圖表區域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 專案時間分佈 */}
        <ProjectDistributionChart
          data={projectDistributionData}
          loading={loading.projectDistribution}
        />

        {/* 團隊成員花費分佈 - 僅管理員可見 */}
        {isAdmin && (
          <TeamCostDistributionChart
            data={teamCostDistributionData}
            loading={loading.teamCostDistribution}
          />
        )}
      </div>

      {/* 工作項目明細表格 */}
      <EnhancedTimeEntriesDetailTable
        filters={filters}
      />

      {/* 頁腳資訊 */}
      <div className="card p-4 bg-secondary-50">
        <div className="flex items-center justify-between text-sm text-secondary-600">
          <div>
            報表時間範圍: {filters.startDate} 至 {filters.endDate}
          </div>
          <div>
            最後更新: {new Date().toLocaleString('zh-TW')}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportsPage
